/**
 * Sink registry + fan-out dispatcher.
 *
 * Mirrors the defensive fan-out in
 * `src/integrations/tanstack-query/root-provider.tsx`: iterate every registered
 * sink, isolate each in a try/catch, and NEVER re-throw — an analytics failure
 * (a broken sink, an ad blocker, a quota error) must never break a user flow.
 * SSR-guarded: a no-op when `window` is undefined.
 *
 * This module owns the mutable sink list (a singleton). Application code never
 * imports it directly — it goes through the `analytics` facade in `index.ts`.
 * Tests reset the registry via `__resetSinksForTest()`.
 */
import type { AnalyticsSink, UserTraits } from "./types";

const sinks: AnalyticsSink[] = [];

/** Register a sink. Called once per sink in `index.ts`. Order is irrelevant. */
export function registerSink(sink: AnalyticsSink): void {
	sinks.push(sink);
}

/** True only in a browser with a live document. */
function canDispatch(): boolean {
	if (typeof window === "undefined") return false;
	// Automated browsers (e2e puppeteer, JS-executing crawlers) — without this,
	// GA4/Amplitude record bot pageviews as real traffic. Same actor-level block
	// as Sentry init (src/integrations/sentry/sentry.ts).
	if (typeof navigator !== "undefined" && navigator.webdriver) return false;
	return true;
}

function enabled(sink: AnalyticsSink): boolean {
	try {
		return sink.isEnabled ? sink.isEnabled() : true;
	} catch {
		return false;
	}
}

/** Run `fn` against every enabled sink, isolating failures per sink. */
function fanOut(fn: (sink: AnalyticsSink) => void): void {
	if (!canDispatch()) return;
	for (const sink of sinks) {
		if (!enabled(sink)) continue;
		try {
			fn(sink);
		} catch (err) {
			// Never let a sink throw into a user flow. Surface it in dev only.
			if (import.meta.env.DEV) {
				console.error(`[analytics] sink "${sink.name}" threw`, err);
			}
		}
	}
}

export function dispatchTrack(
	event: string,
	props: Record<string, unknown>,
): void {
	fanOut((s) => s.track(event, props));
}

export function dispatchIdentify(traits: UserTraits): void {
	fanOut((s) => s.identify?.(traits));
}

export function dispatchPage(path: string): void {
	fanOut((s) => s.page?.(path));
}

export function dispatchReset(): void {
	fanOut((s) => s.reset?.());
}

/** Test-only: clear the registry and optionally seed it with the given sinks. */
export function __resetSinksForTest(seed: AnalyticsSink[] = []): void {
	sinks.length = 0;
	sinks.push(...seed);
}
